import axios from "axios";
import AppConstants from "./contants";
import { KeyValuePair, PagedResponse } from "./common";
import { Log, LogFilter, LogIgnore, LogIgnoreFilter, LogStatistics } from "./log";
import { AuthToken } from "./user";

const client = axios.create({
    baseURL: `${AppConstants.API_BASE_URL}`,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

client.interceptors.request.use(
    (config) => {
        const storedToken = localStorage.getItem('auth');
        if (storedToken) {
            const authToken = JSON.parse(storedToken) as AuthToken;
            config.headers['Authorization'] = `Bearer ${authToken.token}`;
        }

        return config;
    },

    (error) => {
        return Promise.reject(error);
    }
);

client.interceptors.response.use((response) => response, (error) => {
    if (axios.isAxiosError(error)) {
        if (error.response) {
            if (error.response.status === 401 || error.response.status === 403) {
                window.location.href = ('/login');
            }
        }
    }
    return Promise.reject(error);
});

export default client

export const handleError = ({ error }: { error: unknown | any; }): string => {
    if (axios.isAxiosError(error)) {
        console.log('error message: ', error.message);
        return error.message;
    } else {
        console.log('unexpected error: ', error);
        return 'An unexpected error occurred';
    }
}


export const getAllApp = async (module: string): Promise<Record<number, string> | string> => {
    const apps: Record<number, string> = {};

    try {
        const response = await client.get(`/common/apps?module=${module ?? ""}`);
        const data = response.data as KeyValuePair<number, string>[]

        data.forEach(item => {
            apps[item.key] = item.value;
        });

    } catch (error) {
        return handleError({ error })
    }

    return apps;
}

export const getAllProfileByAppID = async (appId: number, module: string): Promise<Record<number, string> | string> => {
    const profiles: Record<number, string> = {};

    try {
        const response = await client.get(`/common/profiles?module=${module ?? ""}&appId=${appId}`);
        const data = response.data as KeyValuePair<number, string>[]

        data.forEach(item => {
            profiles[item.key] = item.value;
        });
    } catch (error) {
        return handleError({ error })
    }

    return profiles;
}

export const getAllMachine = async (module: string): Promise<string[] | string> => {
    try {
        const response = await client.get(`/log/machines?module=${module ?? ""}`);
        const machines = response.data as string[];
        return machines;
    } catch (error) {
        return handleError({ error })
    }
}

export const getLogStatistics = async (): Promise<LogStatistics[] | string> => {
    try {
        const requestUrl = `${AppConstants.API_BASE_URL}/log/statistics`
        const response = await client.get(requestUrl);
        const logStatistics = response.data as LogStatistics[];
        return logStatistics;
    } catch (error) {
        return handleError({ error })
    }
}

export const getLogByFilter = async (logFilter?: LogFilter, module?: string, pageIndex?: number, pageSize?: number): Promise<PagedResponse<Log> | string> => {
    try {
        const requestUrl = `${AppConstants.API_BASE_URL}/log/${pageSize ?? 0}/${pageIndex ?? 0}?module=${module ?? ""}`
        const response = await client.post(requestUrl, logFilter);
        const result = response.data as PagedResponse<Log>;
        return result;
    } catch (error) {
        return handleError({ error })
    }
}

export const getLogById = async (logId: string, module: string): Promise<Log | string> => {
    try {
        const requestUrl = `${AppConstants.API_BASE_URL}/log/${logId}?module=${module ?? ""}`
        const response = await client.get(requestUrl);
        const log = response.data as Log;
        return log;
    } catch (error) {
        return handleError({ error })
    }
}

export const deleteLogById = async (logId: string, module: string): Promise<boolean | string> => {
    try {
        const response = await client.post(`/log/remove?module=${module ?? ""}`, [logId]);
        if (response.status === 200) return true;
    } catch (error) {
        return handleError({ error })
    }

    return false;
}

export const deleteLogByFilter = async (logFilter: LogFilter, module: string): Promise<boolean | string> => {
    try {
        const response = await client.post(`/log/remove-by-filter?module=${module ?? ""}`, logFilter);
        if (response.status === 200) return true;
    } catch (error) {
        return handleError({ error })
    }

    return false;
}

export const getLogIgnoreByFilter = async (logIgnoreFilter?: LogIgnoreFilter, module?: string, pageIndex?: number, pageSize?: number): Promise<PagedResponse<LogIgnore> | string> => {
    try {
        const requestUrl = `${AppConstants.API_BASE_URL}/log/ignore-Keyword/${pageSize ?? 0}/${pageIndex ?? 0}?module=${module ?? ""}`
        const response = await client.post(requestUrl, logIgnoreFilter);
        const result = response.data as PagedResponse<LogIgnore>;
        return result;
    } catch (error) {
        return handleError({ error })
    }
}

export const getLogIgnoreById = async (id: string, module: string): Promise<LogIgnore | string> => {
    try {
        const requestUrl = `${AppConstants.API_BASE_URL}/log/ignore-Keyword/${id}?module=${module ?? ""}`
        const response = await client.get(requestUrl);
        const log = response.data as LogIgnore;
        return log;
    } catch (error) {
        return handleError({ error })
    }
}

export const deleteLogIgnoreById = async (id: string, module: string): Promise<boolean | string> => {
    try {
        const response = await client.post(`/log/ignore-Keyword/remove?module=${module ?? ""}`, [id]);
        if (response.status === 200) return true;
    } catch (error) {
        return handleError({ error })
    }

    return false;
}

export const addLogIgnoreById = async (module: string, keyword?: string, comment?: string, active: boolean = false): Promise<LogIgnore | string> => {
    try {
        const requestUrl = `/log/ignore-Keyword/create?module=${module ?? ""}`
        const response = await client.post(requestUrl, { keyword: keyword, comment: comment, active: active });
        return response.data as LogIgnore;
    } catch (error) {
        return handleError({ error })
    }
}

export const updateLogIgnoreById = async (module: string, id: string, keyword?: string, comment?: string, active: boolean = false): Promise<LogIgnore | string> => {
    try {
        const requestUrl = `/log/ignore-Keyword/update/${id}?module=${module ?? ""}`
        const response = await client.post(requestUrl, { id: id, keyword: keyword, comment: comment, active: active });
        return response.data as LogIgnore;  
    } catch (error) {
        return handleError({ error })
    }
}